import { Box, Button, Heading, Modal, Paragraph, ZillowLogo } from '@zillow/constellation-10';
import { IconArrowLeftFilled } from '@zillow/constellation-icons';
import type { Brand } from '../../../types/brand';
import HotPadsLogo from '../../icons/HotPadsLogo';
import TruncatedUrlDisplay from '../../TruncatedUrlDisplay';
import { isInternalUrl, normalizeUrl } from '../../../utils/urlUtils';

export type ExternalLinkWarningData = {
  disclaimerTitle?: string;
  disclaimerText?: string;
  primaryCta?: {
    type?: string;
    label?: string;
    icon?: string;
  };
  allowListedDomains?: Array<string>;
};

export type ExternalLinkWarningModalProps = {
  brand?: Brand;
  isOpen?: boolean;
  onClose(): void;
  url: string;
  displayText?: string;
  warningData?: ExternalLinkWarningData;
  onExternalLinkClick?: () => void;
};

/**
 * Renders the appropriate brand logo based on the current brand
 */
function BrandLogo({ brand }: { brand: Brand }) {
  if (brand === 'hotpads') {
    return (
      <Box
        css={{
          height: '32px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <HotPadsLogo width={120} height={32} data-testid="hotpads-logo" />
      </Box>
    );
  }

  return (
    <Box
      css={{
        height: '32px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <ZillowLogo role="img" aria-label="Zillow" />
    </Box>
  );
}

export default function ExternalLinkWarningModal({
  brand = 'zillow',
  isOpen = true,
  onClose,
  url,
  displayText,
  warningData,
  onExternalLinkClick,
}: ExternalLinkWarningModalProps) {
  // Don't show modal for internal URLs
  if (isInternalUrl(url, warningData?.allowListedDomains)) {
    return null;
  }

  const handleLinkClick = () => {
    // Trigger clickstream event for external link click
    onExternalLinkClick?.();

    // Close modal and open link in new tab
    onClose();

    // Normalize URL to ensure it has a protocol
    const normalizedUrl = normalizeUrl(url) || url;
    window.open(normalizedUrl, '_blank', 'noopener,noreferrer');
  };

  const handleGoBack = () => {
    onClose();
  };

  // Use warning data if provided, otherwise fall back to defaults
  const brandName = brand === 'hotpads' ? 'HotPads' : 'Zillow';
  const headingText = warningData?.disclaimerTitle || `You're leaving ${brandName}`;
  const bodyText = warningData?.disclaimerText || 'If you trust this link, select it to continue.';
  const backButtonLabel = warningData?.primaryCta?.label || 'Go back';

  return (
    <Modal
      open={isOpen}
      onOpenChange={(open) => !open && handleGoBack()}
      size={{
        base: 'fullScreen',
        md: 'fullScreen',
      }}
      body={
        <Box
          css={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            textAlign: 'center',
            height: '100%',
            width: '100%',
            maxWidth: '100%',
            minWidth: 0,
            paddingInline: 'default',
            paddingBlock: 'loose',
            overflow: 'hidden',
            boxSizing: 'border-box',
          }}
        >
          <BrandLogo brand={brand} />

          <Heading level={2} textStyle="heading-xs" css={{ marginBottom: 'default', marginTop: 'looser' }}>
            {headingText}
          </Heading>

          <Paragraph css={{ marginBottom: 'default', width: { base: '274px', md: 'auto' } }}>{bodyText}</Paragraph>

          <TruncatedUrlDisplay url={url} displayText={displayText} onClick={handleLinkClick} />

          <Button
            tone="neutral"
            emphasis="outlined"
            onClick={handleGoBack}
            icon={<IconArrowLeftFilled />}
            iconPosition="start"
          >
            {backButtonLabel}
          </Button>
        </Box>
      }
    />
  );
}
